import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Phone } from "lucide-react";
import { api } from "../lib/api";
import { trackChatbotLead, track } from "../lib/tracking";

const STORAGE_KEY = "alpha_chat_session";

// Stable, monotonic local id per message (used as React key — never serialised)
let _msgSeq = 0;
const newMsg = (role, content) => ({ _id: ++_msgSeq, role, content });

const WELCOME = newMsg(
  "assistant",
  "Hi, I'm Alpha — happy to help. I work alongside our intake team, so whether you're a participant, a family member, or a support coordinator, I can point you in the right direction in plain English. What's on your mind today?",
);

// Conversational opener prompts — sent as a real user message so the LLM
// continues the conversation naturally (no scripted replies).
const QUICK_STARTS = [
  { id: "browse", label: "Browse our SIL homes", text: "Can you tell me about your SIL homes and current vacancies?" },
  { id: "refer", label: "Start a referral", text: "How do I make a referral for a participant? I'd like to start the process." },
  { id: "intake", label: "Speak to intake", text: "I'd like someone from your intake team to call me — what info do you need?" },
  { id: "services", label: "What services do you offer?", text: "What NDIS services does Alpha provide, and how are they delivered?" },
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([WELCOME]);
  const [leadSent, setLeadSent] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) {
      setSessionId(existing);
      api
        .get(`/chat/history/${existing}`)
        .then((r) => {
          const hist = (r.data.messages || []).map((m) => newMsg(m.role, m.content));
          if (hist.length) setMessages([WELCOME, ...hist]);
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e, override) => {
    e?.preventDefault();
    const text = (override ?? input).trim();
    if (!text || sending) return;
    setMessages((m) => [...m, newMsg("user", text)]);
    if (override === undefined) setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", { message: text, session_id: sessionId || undefined });
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages((m) => [...m, newMsg("assistant", data.reply)]);
      track("chatbot_message", { length: text.length });
      if (data.captured && (data.captured.email || data.captured.phone) && !leadSent) {
        trackChatbotLead({ session_id: data.session_id, ...data.captured });
        setLeadSent(true);
      }
    } catch (err) {
      setMessages((m) => [
        ...m,
        newMsg(
          "assistant",
          "Sorry — I can't respond right now. Please call 1300 380 692 or submit our referral form and we'll reply within one business day.",
        ),
      ]);
    } finally {
      setSending(false);
    }
  };

  const sendQuick = (preset) => {
    track("chatbot_quickstart", { id: preset.id });
    send(undefined, preset.text);
  };

  const toggle = () => {
    setOpen((v) => {
      if (!v) track("chatbot_open", {});
      return !v;
    });
  };

  return (
    <>
      <button
        onClick={toggle}
        aria-label={open ? "Close chat" : "Open Alpha chat"}
        data-testid="chatbot-toggle"
        className="fixed bottom-6 left-6 md:left-8 md:bottom-8 z-50 w-14 h-14 rounded-full alpha-gradient text-white shadow-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-24 left-4 right-4 md:left-8 md:right-auto md:w-[380px] z-50 alpha-glass rounded-2xl overflow-hidden shadow-2xl border border-[color:var(--alpha-line-strong)] flex flex-col max-h-[70vh]"
        >
          <div className="px-5 py-4 alpha-gradient text-white flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="font-display font-semibold">Alpha · Intake assistant</div>
              <div className="text-[11px] tracking-[0.18em] uppercase opacity-80 inline-flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[color:var(--alpha-green-400)] opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[color:var(--alpha-green-400)]" />
                </span>
                Online · replies in seconds
              </div>
            </div>
            <a
              href="tel:1300380692"
              className="text-white/90 hover:text-white"
              aria-label="Call 1300 380 692"
              data-testid="chatbot-phone"
            >
              <Phone className="w-4 h-4" />
            </a>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white"
            data-testid="chatbot-messages"
          >
            {messages.map((m) => (
              <div
                key={m._id}
                className={`max-w-[85%] text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[color:var(--alpha-purple-600)] text-white rounded-2xl rounded-br-md px-4 py-2.5"
                    : "mr-auto bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-ink)] rounded-2xl rounded-bl-md px-4 py-2.5 border border-[color:var(--alpha-purple-100)]"
                }`}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div className="mr-auto max-w-[85%] text-sm bg-[color:var(--alpha-purple-50)] rounded-2xl rounded-bl-md px-4 py-3 border border-[color:var(--alpha-purple-100)] inline-flex gap-1">
                <span className="w-1.5 h-1.5 bg-[color:var(--alpha-purple-600)] rounded-full alpha-typing-dot" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-[color:var(--alpha-purple-600)] rounded-full alpha-typing-dot" style={{ animationDelay: "120ms" }} />
                <span className="w-1.5 h-1.5 bg-[color:var(--alpha-purple-600)] rounded-full alpha-typing-dot" style={{ animationDelay: "240ms" }} />
              </div>
            )}

            {/* Quick starts — show only at the very beginning so they don't clutter the thread */}
            {messages.length <= 1 && !sending && (
              <div className="pt-1" data-testid="chatbot-quickstarts">
                <div className="text-[10.5px] tracking-[0.2em] uppercase font-semibold text-[color:var(--alpha-mute)] mb-2 px-1">
                  Or pick one
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_STARTS.map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => sendQuick(q)}
                      data-testid={`chatbot-quick-${q.id}`}
                      className="text-left text-[12.5px] leading-tight font-medium text-[color:var(--alpha-purple-700)] bg-white border border-[color:var(--alpha-purple-200)] hover:bg-[color:var(--alpha-purple-50)] rounded-full py-1.5 px-3 transition-colors"
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-[color:var(--alpha-line)] bg-white p-3 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              className="flex-1 h-11 rounded-full border border-[color:var(--alpha-line-strong)] px-4 text-sm focus:outline-none focus:border-[color:var(--alpha-purple-600)]"
              data-testid="chatbot-input"
              disabled={sending}
              aria-label="Chat message"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="w-11 h-11 rounded-full alpha-gradient text-white flex items-center justify-center disabled:opacity-50"
              data-testid="chatbot-send"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
